import { useState } from "react";
import { Eye, Loader2, AlertTriangle, CheckCircle2, Users2, Smartphone, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WhatsAppPreview from "@/components/WhatsAppPreview";

export function CampaignPreviewButton({ campaign }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const openPreview = async () => {
    setOpen(true); setData(null); setError(null); setSent(null);
    try {
      const r = await api.get(`/campaigns/${campaign.id}/preview`);
      setData(r.data); setPhone(r.data.test_phone || "");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Anteprima non disponibile"); }
  };

  const sendTest = async () => {
    setSending(true); setSent(null);
    try {
      const { data: r } = await api.post(`/campaigns/${campaign.id}/send-test`, { phone });
      setSent({ ok: true, phone: r.phone });
      toast.success(`Messaggio reale inviato a ${r.phone}`);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Invio non riuscito";
      setSent({ ok: false, msg }); toast.error(msg);
    } finally { setSending(false); }
  };

  const isSms = data?.channel === "sms";
  const hdr = data?.header?.type && data.header.type !== "none" ? data.header : null;
  const shownHeader = hdr && !data?.image_will_send && ["image", "video", "document"].includes(hdr.type) ? null : hdr;

  return (
    <>
      <button onClick={openPreview} data-testid={`campaign-preview-${campaign.id}`} title="Anteprima del messaggio finale"
              className="text-slate-500 hover:text-[#10B981]"><Eye className="h-4 w-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B1120] border-[#1F2937] text-slate-100 max-w-md" data-testid="campaign-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">Cosa riceveranno i clienti</DialogTitle></DialogHeader>
          {!data && !error && <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
          {error && <p className="text-sm text-[#FCA5A5]" data-testid="campaign-preview-error">{error}</p>}
          {data && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="campaign-preview-recipient">
                <Users2 className="h-3.5 w-3.5" /> Esempio compilato su <b className="text-slate-200">{data.recipient.name}</b>
                {data.recipients > 0 && <span>· {data.recipients} destinatari</span>}
                {data.template?.meta_name && <span className="font-mono2">· {data.template.meta_name}</span>}
              </p>
              {data.header_mismatch && (
                <div className="rounded-lg border border-[#EF4444]/50 bg-[#EF4444]/10 px-3 py-2 text-xs text-[#FCA5A5] flex gap-2" data-testid="campaign-preview-mismatch">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span><b>L'immagine NON partirà.</b> {data.header_mismatch}</span>
                </div>
              )}
              {!data.header_mismatch && data.image_will_send && (
                <p className="text-xs text-[#6EE7B7] flex items-center gap-1.5" data-testid="campaign-preview-image-ok"><CheckCircle2 className="h-3.5 w-3.5" /> Immagine inclusa nel messaggio</p>
              )}
              {isSms ? (
                <div className="rounded-2xl bg-[#111827] border border-[#1F2937] p-4 text-sm text-slate-100 whitespace-pre-wrap" data-testid="campaign-preview-sms">{data.sms_text || data.body}</div>
              ) : (
                <WhatsAppPreview body={data.body} header={shownHeader} footer={data.footer} buttons={data.buttons} carousel={data.carousel} name={data.recipient.name} />
              )}
              {!isSms && (
                <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-3 space-y-2" data-testid="campaign-send-test-box">
                  <p className="text-xs font-medium text-slate-200 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-[#10B981]" /> Prova sul mio numero</p>
                  <p className="text-[11px] text-slate-500">Invia davvero questo messaggio (template approvato, immagine e pulsanti) al tuo telefono prima di lanciare la campagna. Costa come un messaggio normale.</p>
                  <div className="flex gap-2">
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" disabled={!data.can_send_test || sending}
                           className="bg-[#0A0E17] border-[#1F2937] h-9 text-slate-100" data-testid="campaign-send-test-phone" />
                    <Button onClick={sendTest} disabled={!data.can_send_test || sending || !phone.trim()} data-testid="campaign-send-test-button"
                            className="h-9 bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold shrink-0">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Invia prova</>}
                    </Button>
                  </div>
                  {!data.can_send_test && !data.header_mismatch && <p className="text-[11px] text-amber-400" data-testid="campaign-send-test-unavailable">Prova reale non disponibile: serve un template Meta approvato e WhatsApp collegato.</p>}
                  {sent?.ok && <p className="text-[11px] text-[#6EE7B7] flex items-center gap-1" data-testid="campaign-send-test-ok"><CheckCircle2 className="h-3.5 w-3.5" /> Inviato a {sent.phone}: controlla il telefono.</p>}
                  {sent && !sent.ok && <p className="text-[11px] text-[#FCA5A5]" data-testid="campaign-send-test-error">{sent.msg}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
